// src/stores/modalStore.js
import { defineStore } from 'pinia'
import { hasModalKey } from '@/config/modalComponentRegistry'

export const useModalStore = defineStore('modal', {
  state: () => ({
    isOpen: false,
    componentKey: null, // canonical registry key
    isStandalone: false,
    modalProps: {}, // normalized prop bag passed to the modal component via v-bind
    onSave: null, // optional callback provided by the opener
  }),

  getters: {
    // Consumers can spread this into the modal host (<component ... v-bind="payload" />)
    payload: s => ({
      componentKey: s.componentKey,
      isStandalone: s.isStandalone,
      modalProps: s.modalProps,
    }),
  },

  actions: {
    /**
     * Safely open a modal by key. If the key is missing/invalid, we NO-OP.
     * Accepts both `modalProps` and `props` (alias) and normalizes to `modalProps`.
     * Returns true if opened, false otherwise.
     */
    openModal({
      componentKey,
      isStandalone = false,
      modalProps = {},
      // alias accepted so callers can use `props: {...}` without caring about store internals
      props = undefined,
      onSave = null,
    } = {}) {
      if (!componentKey || !hasModalKey(componentKey)) {
        console.warn('[modalStore] openModal blocked: invalid componentKey:', componentKey)
        return false
      }

      // Normalize props: prefer explicit `props` if provided, else `modalProps`
      let finalProps = {}
      if (props && typeof props === 'object') {
        finalProps = props
      } else if (modalProps && typeof modalProps === 'object') {
        finalProps = modalProps
      } else if (props != null || modalProps != null) {
        console.warn('[modalStore] openModal: props provided but not an object; ignoring.')
      }

      // Normalize onSave
      const finalOnSave = typeof onSave === 'function' ? onSave : null

      this.componentKey = componentKey
      this.isStandalone = !!isStandalone
      this.modalProps = finalProps
      this.onSave = finalOnSave
      this.isOpen = true
      return true
    },

    closeModal() {
      this.isOpen = false
      this.componentKey = null
      this.isStandalone = false
      this.modalProps = {}
      this.onSave = null
    },

    /** Escape hatch if something wedges the modal open */
    forceClose() {
      this.closeModal()
    },

    /** Update current modal props without re-opening */
    setModalProps(next = {}) {
      if (!this.isOpen) return
      if (!next || typeof next !== 'object') {
        console.warn('[modalStore] setModalProps called with non-object; ignoring.')
        return
      }
      this.modalProps = { ...this.modalProps, ...next }
    },
  },
})
